import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Typography,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { formatConfigValue } from '../configUtil';
import type { ConfigDiffEntry } from '../configTypes';
import styles from './configDiffDialog.module.css';

const diffType = (
  entry: ConfigDiffEntry
): { label: string; color: 'info' | 'warning' | 'error' } => {
  if (entry.removed) return { label: 'Removed', color: 'error' };
  if (entry.reset) return { label: 'Reset to default', color: 'warning' };
  return { label: 'Edited', color: 'info' };
};

interface ConfigDiffDialogProps {
  open: boolean;
  deviceName: string;
  diff: ConfigDiffEntry[];
  overwrite: boolean;
  deploying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfigDiffDialog: React.FC<ConfigDiffDialogProps> = ({
  open,
  deviceName,
  diff,
  overwrite,
  deploying,
  onConfirm,
  onCancel,
}) => {
  const changeCount = diff.length;

  return (
    <Dialog open={open} onClose={deploying ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {overwrite
          ? `Overwrite configuration for ${deviceName}`
          : `Deploy ${changeCount} ${changeCount === 1 ? 'change' : 'changes'} to ${deviceName}`}
      </DialogTitle>

      <DialogContent>
        {overwrite && (
          <Alert severity="warning" className={styles.overwriteWarning}>
            This replaces the device's entire configuration with the values
            below. Fields you didn't set are reset to their schema default
            (shown as “Reset to default”). Use this only when you intend to reset
            the full configuration.
          </Alert>
        )}

        {changeCount === 0 ? (
          <Typography variant="body2">
            No changes to deploy — the edited values match the device's current
            configuration.
          </Typography>
        ) : (
          <Table size="small" className={styles.diffTable}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Current</TableCell>
                <TableCell>New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diff.map((entry) => {
                const configFieldDiff = diffType(entry);
                return (
                  <TableRow key={entry.field}>
                    <TableCell className={styles.fieldCell}>{entry.field}</TableCell>
                    <TableCell>
                      <Chip label={configFieldDiff.label} color={configFieldDiff.color} size="small" />
                    </TableCell>
                    <TableCell className={styles.currentCell}>
                      {formatConfigValue(entry.current)}
                    </TableCell>
                    <TableCell
                      className={entry.removed ? styles.removedCell : styles.nextCell}
                    >
                      {entry.removed ? 'removed' : formatConfigValue(entry.next)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={deploying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={overwrite ? 'warning' : 'primary'}
          onClick={onConfirm}
          disabled={deploying || changeCount === 0}
        >
          {deploying ? 'Deploying…' : 'Deploy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigDiffDialog;
